import React, {createContext, type ReactNode, useEffect, useState} from "react";
import {apiEndpoint} from "@/components/Providers/JWTProvider.tsx";

type LoggedOutFeeType = {
    feeId: string | null;
    setFeeId: (feeId: string | null) => void;
    loading: boolean;
    notFound: boolean;
    loadFailed: boolean;
    fee: null | LoggedOutFee;
};

export type LoggedOutFee = {
    id: string;
    name: string;
    saleEndDateEmployeeEnded: boolean;
    saleEndDateStudentEnded: boolean;
    saleEndDateCommunityMemberEnded: boolean;
    saleEndDateGuestEnded: boolean;
};

const LoggedOutFeeContext = createContext<LoggedOutFeeType>({
    feeId: null,
    setFeeId: () => {},
    loading: true,
    notFound: false,
    loadFailed: false,
    fee: null,
});

export const LoggedOutFeeProvider = ({children}: {children: ReactNode}) => {
    const [feeId, setFeeId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [fee, setFee] = useState<LoggedOutFee | null>(null);
    const [notFound, setNotFound] = useState<boolean>(false);
    const [loadFailed, setLoadFailed] = useState<boolean>(false);

    useEffect(() => {
        if (!feeId) {
            setLoading(false);
            setFee(null);
            setNotFound(false);
            setLoadFailed(false);
        }
    }, [feeId]);

    useEffect(() => {
        if (!feeId) {
            return;
        }
        let superseded = false;
        setLoading(true);
        setNotFound(false);
        setLoadFailed(false);
        const url = new URL(`/v1/settings/optional-fee/${encodeURIComponent(feeId)}`, apiEndpoint);
        fetch(url)
            .then(async res => {
                if (superseded) {
                    return;
                }
                if (res.ok) {
                    setFee(await res.json());
                } else {
                    console.error("error fetching fee", res.status);
                    setFee(null);
                    if (res.status === 404) {
                        setNotFound(true);
                    } else {
                        setLoadFailed(true);
                    }
                }
                setLoading(false);
            })
            .catch(error => {
                if (superseded) {
                    return;
                }
                console.error("error fetching fee", error);
                setFee(null);
                setLoadFailed(true);
                setLoading(false);
            });

        return () => {
            superseded = true;
        };
    }, [feeId]);

    return (
        <LoggedOutFeeContext.Provider
            value={{
                feeId,
                setFeeId,
                fee,
                loading,
                notFound,
                loadFailed,
            }}
        >
            {children}
        </LoggedOutFeeContext.Provider>
    );
};

export const useLoggedOutFee = (): LoggedOutFeeType => {
    const context = React.useContext(LoggedOutFeeContext);
    if (Object.keys(context).length === 0) {
        throw new Error("useStripeTerminal must be used within a StripeTerminalContextProvider");
    }
    return context;
};
